import { useAuth } from "@/_core/hooks/useAuth";
import { trackNavigation, trackDashboardView } from "@/lib/sentry";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import {
  Activity,
  BarChart3,
  Brain,
  ChevronRight,
  ClipboardList,
  DollarSign,
  Film,
  FileText,
  LayoutDashboard,
  LogOut,
  Megaphone,
  PanelLeft,
  PieChart,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Tag,
  TrendingUp,
  UserCheck,
  Users,
  Wand2,
  Zap,
  Library,
  Handshake,
  Link2,
  FolderOpen,
  HelpCircle,
  MessageCircle,
  Wallet,
  Crosshair,
  Package,
  Layers,
  Bot,
} from "lucide-react";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";

type UserRole = "admin" | "diretoria" | "gestao" | "user" | "influencer" | "affiliate" | "affiliate_manager";

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  roles: UserRole[];
}

interface MenuGroup {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  roles: UserRole[];
  items: MenuItem[];
}

// Standalone items (no submenu)
const standaloneItems: MenuItem[] = [
  { icon: LayoutDashboard, label: "Visão Geral", path: "/", roles: ["admin", "diretoria"] },
  { icon: Bot, label: "Minha IA", path: "/minha-ia", roles: ["admin", "diretoria", "gestao", "user", "influencer", "affiliate", "affiliate_manager"] },
];

// Grouped items with submenus
const menuGroups: MenuGroup[] = [
  {
    id: "financeiro",
    icon: DollarSign,
    label: "Financeiro",
    roles: ["admin", "diretoria"],
    items: [
      { icon: TrendingUp, label: "Investimento & ROI", path: "/diretoria/roi", roles: ["admin", "diretoria"] },
      { icon: ShoppingCart, label: "Vendas Gerais", path: "/diretoria/bling", roles: ["admin", "diretoria"] },
      { icon: BarChart3, label: "Bling Lille", path: "/diretoria/bling-lille", roles: ["admin", "diretoria"] },
      { icon: UserCheck, label: "Primeira Compra", path: "/diretoria/primeira-compra", roles: ["admin", "diretoria"] },
      { icon: Tag, label: "Desconto & Frete", path: "/diretoria/desconto-frete", roles: ["admin", "diretoria"] },
      { icon: PieChart, label: "Gastos Pessoa & Produto", path: "/diretoria/gastos", roles: ["admin", "diretoria"] },
      { icon: Users, label: "LTV (Lifetime Value)", path: "/diretoria/ltv", roles: ["admin", "diretoria"] },
      { icon: BarChart3, label: "Análise de Cohort", path: "/diretoria/cohort", roles: ["admin", "diretoria"] },
      { icon: TrendingUp, label: "Taxa de Conversão", path: "/diretoria/conversao", roles: ["admin", "diretoria"] },
      { icon: Package, label: "Mercado Livre", path: "/diretoria/mercado-livre", roles: ["admin", "diretoria"] },
    ],
  },
  {
    id: "performance",
    icon: Megaphone,
    label: "Performance",
    roles: ["admin", "diretoria", "gestao"],
    items: [
      { icon: BarChart3, label: "Tráfego & Analytics", path: "/diretoria/trafego", roles: ["admin", "diretoria"] },
      { icon: Layers, label: "Contas de Anúncios", path: "/gestao/contas-de-anuncios", roles: ["admin", "diretoria", "gestao"] },
      { icon: Film, label: "Criativos", path: "/gestao/criativos", roles: ["admin", "diretoria", "gestao"] },
      { icon: Zap, label: "Hook & Hold Rate", path: "/gestao/hook-rate", roles: ["admin", "diretoria", "gestao"] },
      { icon: Activity, label: "Elasticidade de Escala", path: "/gestao/elasticidade", roles: ["admin", "diretoria", "gestao"] },
    ],
  },
  {
    id: "conteudo",
    icon: FileText,
    label: "Conteúdo",
    roles: ["admin", "diretoria", "gestao"],
    items: [
      { icon: FileText, label: "Análise de Roteiros", path: "/gestao/roteiros", roles: ["admin", "diretoria", "gestao"] },
      { icon: Wand2, label: "Gerador de Roteiros", path: "/gestao/gerador-roteiros", roles: ["admin", "diretoria", "gestao"] },
      { icon: Sparkles, label: "Gerador por Referências", path: "/gestao/gerador-roteiros-referencias", roles: ["admin", "diretoria", "gestao"] },
      { icon: Library, label: "Biblioteca de Roteiros", path: "/gestao/biblioteca-roteiros", roles: ["admin", "diretoria", "gestao"] },
      { icon: ClipboardList, label: "Ativações de Mídia", path: "/gestao/tasks", roles: ["admin", "diretoria", "gestao"] },
    ],
  },
  {
    id: "parceiro",
    icon: Handshake,
    label: "Afiliados",
    roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"],
    items: [
      { icon: Users, label: "Gestão de Afiliados", path: "/gestao/afiliados", roles: ["admin", "diretoria", "affiliate_manager"] },
      { icon: FileText, label: "Importar Vendas Pharmacy", path: "/gestao/importar-pharmacy", roles: ["admin", "diretoria", "affiliate_manager"] },
      { icon: BarChart3, label: "Dashboard Gestor", path: "/gestao/gestor-dashboard", roles: ["admin", "diretoria", "affiliate_manager"] },
      { icon: Users, label: "MGM Afiliados", path: "/gestao/sub-portfolios", roles: ["admin", "diretoria", "affiliate_manager"] },
      { icon: Crosshair, label: "Tracking & Atribuição", path: "/diretoria/tracking", roles: ["admin", "diretoria", "affiliate_manager"] },
      { icon: LayoutDashboard, label: "Dashboard", path: "/parceiro/dashboard", roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"] },
      { icon: ShoppingCart, label: "Minhas Vendas", path: "/parceiro/vendas", roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"] },
      { icon: Wallet, label: "Comissões", path: "/parceiro/comissoes", roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"] },
      { icon: Link2, label: "Links e Cupons", path: "/parceiro/cupons", roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"] },
      { icon: FolderOpen, label: "Materiais", path: "/parceiro/materiais", roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"] },
      { icon: ClipboardList, label: "Entregas", path: "/parceiro/entregas", roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"] },
      { icon: HelpCircle, label: "Suporte", path: "/parceiro/suporte", roles: ["admin", "diretoria", "gestao", "affiliate", "affiliate_manager"] },
    ],
  },
  {
    id: "comercial",
    icon: MessageCircle,
    label: "Comercial",
    roles: ["admin", "diretoria", "gestao"],
    items: [
      { icon: MessageCircle, label: "WhatsApp", path: "/comercial/whatsapp", roles: ["admin", "diretoria", "gestao"] },
      { icon: Users, label: "Leads Black Friday", path: "/comercial/black-friday", roles: ["admin", "diretoria", "gestao"] },
    ],
  },
  {
    id: "admin",
    icon: ShieldCheck,
    label: "Administração",
    roles: ["admin"],
    items: [
      { icon: Users, label: "Usuários", path: "/admin/usuarios", roles: ["admin"] },
      { icon: Brain, label: "System Prompts", path: "/admin/prompts", roles: ["admin"] },
      { icon: Settings, label: "Configurações", path: "/admin/config", roles: ["admin"] },
    ],
  },
];

// Build a flat list for active detection
function getAllItems(): MenuItem[] {
  const items = [...standaloneItems];
  for (const group of menuGroups) {
    items.push(...group.items);
  }
  return items;
}

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const OPEN_GROUPS_KEY = "sidebar-open-groups";
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

function getRoleBadge(role: UserRole) {
  const config: Record<UserRole, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
    admin: { label: "Admin", variant: "destructive" },
    diretoria: { label: "Diretoria", variant: "default" },
    gestao: { label: "Gestão", variant: "secondary" },
    affiliate: { label: "Parceiro", variant: "secondary" },
    affiliate_manager: { label: "Gestor Afiliados", variant: "secondary" },
    user: { label: "Usuário", variant: "outline" },
    influencer: { label: "Influenciador", variant: "outline" },
  };
  return config[role] || config.user;
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    window.location.href = "/login";
    return <DashboardLayoutSkeleton />;
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const userRole = (user?.role as UserRole) || "user";

  // Redirect affiliate/affiliate_manager users to their dashboard if they land on home
  useEffect(() => {
    if (userRole === "affiliate" && location === "/") {
      setLocation("/parceiro/dashboard");
    }
    if (userRole === "affiliate_manager" && location === "/") {
      setLocation("/gestao/afiliados");
    }
  }, [userRole, location, setLocation]);

  // Fetch dynamic permissions from DB
  const { data: allowedPaths } = trpc.permissions.mine.useQuery(undefined, {
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  // Filter groups and items by DB permissions (source of truth) or role-based fallback
  const visibleGroups = useMemo(() => {
    return menuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          // Admin always sees everything
          if (userRole === "admin") return true;
          // If DB permissions loaded, use them as single source of truth
          // This ensures that when admin grants a page to a role/user,
          // it appears in the menu regardless of the static group.roles config
          if (allowedPaths) return allowedPaths.includes(item.path);
          // Fallback: use static role-based config (before DB loads)
          return group.roles.includes(userRole) && item.roles.includes(userRole);
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [userRole, allowedPaths]);

  const visibleStandaloneItems = useMemo(() => {
    return standaloneItems.filter((item) => {
      if (userRole === "admin") return true;
      if (allowedPaths) return allowedPaths.includes(item.path);
      return item.roles.includes(userRole);
    });
  }, [userRole, allowedPaths]);

  // Track which groups are open - auto-open the group containing the active page
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(OPEN_GROUPS_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    // Default: open only the group containing the current path (accordion)
    const defaults: Record<string, boolean> = {};
    let foundActive = false;
    for (const group of menuGroups) {
      const hasActive = group.items.some((item) => item.path === window.location.pathname);
      defaults[group.id] = hasActive && !foundActive;
      if (hasActive) foundActive = true;
    }
    return defaults;
  });

  useEffect(() => {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  // Auto-open group when navigating to a page within it (accordion: only that group)
  useEffect(() => {
    for (const group of menuGroups) {
      if (group.items.some((item) => item.path === location)) {
        setOpenGroups(() => {
          const next: Record<string, boolean> = {};
          for (const g of menuGroups) {
            next[g.id] = g.id === group.id;
          }
          return next;
        });
        break;
      }
    }
  }, [location]);

  const allItems = useMemo(() => getAllItems(), []);
  const activeMenuItem = allItems.find((item) => item.path === location);
  const roleBadge = getRoleBadge(userRole);
  const prevLocationRef = useRef(location);

  // Track navigation changes
  useEffect(() => {
    if (prevLocationRef.current !== location) {
      trackNavigation(prevLocationRef.current, location);
      prevLocationRef.current = location;
    }
    if (activeMenuItem) {
      trackDashboardView(activeMenuItem.label, userRole);
    }
  }, [location, activeMenuItem, userRole]);

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups((prev) => {
      const isCurrentlyOpen = prev[groupId];
      // Accordion: close all groups, then toggle the clicked one
      const next: Record<string, boolean> = {};
      for (const key of Object.keys(prev)) {
        next[key] = false;
      }
      next[groupId] = !isCurrentlyOpen;
      return next;
    });
  };

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar collapsible="icon" className="border-r-0" disableTransition={isResizing}>
          <SidebarHeader className="h-16 justify-center">
            <div className="flex items-center gap-3 px-2 transition-all w-full">
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                aria-label="Toggle navigation"
              >
                <PanelLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              {!isCollapsed ? (
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles className="h-5 w-5 text-primary shrink-0" />
                  <span className="font-semibold tracking-tight truncate text-sm">
                    Inteligência Criativa
                  </span>
                </div>
              ) : null}
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-0">
            {/* Standalone items (Visão Geral) */}
            {visibleStandaloneItems.length > 0 && (
              <SidebarGroup className="py-1">
                <SidebarGroupContent>
                  <SidebarMenu className="px-2">
                    {visibleStandaloneItems.map((item) => {
                      const isActive = location === item.path;
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton
                            isActive={isActive}
                            onClick={() => setLocation(item.path)}
                            tooltip={item.label}
                            className="h-10 transition-all font-normal"
                          >
                            <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {/* Grouped items with collapsible submenus */}
            {visibleGroups.map((group) => {
              const isOpen = openGroups[group.id] ?? false;
              const hasActiveChild = group.items.some((item) => item.path === location);

              return (
                <SidebarGroup key={group.id} className="py-0.5">
                  <SidebarGroupContent>
                    <SidebarMenu className="px-2">
                      <Collapsible open={isOpen} onOpenChange={() => toggleGroup(group.id)}>
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                              tooltip={group.label}
                              className={`h-10 transition-all font-normal ${hasActiveChild && !isOpen ? "text-primary" : ""}`}
                            >
                              <group.icon className={`h-4 w-4 ${hasActiveChild ? "text-primary" : ""}`} />
                              <span className="font-medium">{group.label}</span>
                              <ChevronRight
                                className={`ml-auto h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                              />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {group.items.map((item) => {
                                const isActive = location === item.path;
                                return (
                                  <SidebarMenuSubItem key={item.path}>
                                    <SidebarMenuSubButton
                                      isActive={isActive}
                                      onClick={() => setLocation(item.path)}
                                      className="transition-all"
                                    >
                                      <item.icon className={`h-3.5 w-3.5 ${isActive ? "text-primary" : ""}`} />
                                      <span>{item.label}</span>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              );
            })}
          </SidebarContent>

          <SidebarFooter className="p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border shrink-0">
                    <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate leading-none">
                        {user?.name || "-"}
                      </p>
                      <Badge variant={roleBadge.variant} className="text-[10px] px-1.5 py-0 h-4">
                        {roleBadge.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-xs text-muted-foreground">Perfil: {roleBadge.label}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <div className="flex items-center gap-3">
                <span className="tracking-tight text-foreground text-sm font-medium">
                  {activeMenuItem?.label ?? "Menu"}
                </span>
              </div>
            </div>
          </div>
        )}
        <main className="flex-1 p-4 md:p-6">{children}</main>
      </SidebarInset>
    </>
  );
}
